import React, { useState } from "react";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface FeedbackPanelProps {
  candidateName: string;
  screeningId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function FeedbackPanel({ candidateName, screeningId, isOpen, onClose }: FeedbackPanelProps) {
  const [feedbackName, setFeedbackName] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation("job-details");

  // API hooks
  const screeningApi = useScreeningApi();
  const toast = useToaster();

  const handleSubmitFeedback = () => {
    if (!feedbackName.trim() || !feedbackText.trim()) {
      toast.error(t("jobDetails.feedbackPanel.validation.fillAll"));
      return;
    }

    if (!screeningId) {
      toast.error(t("jobDetails.feedbackPanel.validation.screeningIdRequired"));
      return;
    }

    setIsSubmitting(true);

    const payload = {
      screening_id: screeningId,
      title: feedbackName.trim(),
      description: feedbackText.trim(),
      application_type: 2, // for feedback
      status: 0, // default
    };

    screeningApi.callAddFeedback(
      payload,
      (message: string, resp: any) => {
        toast.success(message || t("jobDetails.feedbackPanel.messages.submitSuccess"));
        // Reset form
        setFeedbackName("");
        setFeedbackText("");
        setIsSubmitting(false);
        // Close panel
        onClose();
      },
      (error: string) => {
        toast.error(error || t("jobDetails.feedbackPanel.messages.submitError"));
        setIsSubmitting(false);
      }
    );
  };

  const handleReset = () => {
    setFeedbackName("");
    setFeedbackText("");
  };

  if (!isOpen) return null;

  return (
    <div className="application-history-overlay">
      <div className="application-history-sidebar">
        <div className="history-header">
          <h3>{t("jobDetails.feedbackPanel.title")}</h3>
          <p>{t("jobDetails.feedbackPanel.subtitle")}</p>
          <button className="close-btn" onClick={onClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        <div className="feedback-form">
          <div className="form-group">
            <label htmlFor="feedback-name" className="form-label">
              {t("jobDetails.feedbackPanel.form.name")}
            </label>
            <input
              type="text"
              id="feedback-name"
              value={feedbackName}
              onChange={(e) => setFeedbackName(e.target.value)}
              placeholder={t("jobDetails.feedbackPanel.form.namePlaceholder")}
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="feedback-text" className="form-label">
              {t("jobDetails.feedbackPanel.form.feedback")}
            </label>
            <textarea
              id="feedback-text"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder={t("jobDetails.feedbackPanel.form.feedbackPlaceholder")}
              className="form-control"
              rows={8}
            />
          </div>

          <div className="feedback-actions">
            <button className="btn-reset-feedback" onClick={handleReset} type="button">
              {t("jobDetails.feedbackPanel.buttons.reset")}
            </button>
            <button
              className="btn-submit-feedback"
              onClick={handleSubmitFeedback}
              type="button"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("jobDetails.feedbackPanel.buttons.submitting") : t("jobDetails.feedbackPanel.buttons.submit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
